import { useState, useEffect } from "react";
import { FileText, RefreshCw, FileDown, Loader2 } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { useCompanyContext } from "../../../context";
import { getReports } from "../../../api/wiki";
import TileReport from "../Wiki/tiles/TileReport";
import ReportPDF, { CONFIG, buildFileName } from "../Wiki/tiles/ReportPDF";
import { useReportTheme } from "./utils/useReportTheme";

/* ── Skeleton ── */

const TileSkeleton = ({ delay = 0 }) => (
  <div
    className="rounded-xl border border-surfaceBorder bg-reortTileBg overflow-hidden"
    style={{ minHeight: 160, animationDelay: `${delay}ms` }}
  >
    <div className="px-5 pt-4 pb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="h-4 w-2/5 rounded-md bg-innerBg animate-pulse" />
      </div>
      <div className="flex gap-2">
        <div className="h-5 w-20 rounded-md bg-innerBg animate-pulse" />
        <div className="h-5 w-14 rounded bg-innerBg animate-pulse" />
      </div>
    </div>
    <div className="h-px mx-5 bg-innerBg" />
    <div className="px-5 pt-3 pb-4 flex flex-col gap-2.5">
      <div className="h-3 w-full rounded bg-innerBg animate-pulse" />
      <div className="h-3 w-5/6 rounded bg-innerBg animate-pulse" />
      <div className="h-3 w-4/6 rounded bg-innerBg animate-pulse" />
    </div>
  </div>
);

/* ── DefaultReportView ── */

const DefaultReportView = ({
  target: targetProp,
  companyName: companyNameProp,
} = {}) => {
  const { currentCompany } = useCompanyContext();
  const t = useReportTheme();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const companyName = companyNameProp || currentCompany?.case || "";
  const target = targetProp || currentCompany?.target || "";

  const loadReports = () => {
    setLoading(true);
    setError(false);
    setVisible(false);
    getReports(target)
      .then((res) => {
        setReports(res?.data || []);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setVisible(true)),
        );
      })
      .catch(() => {
        setReports([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const handleExportAll = async () => {
    if (isExporting || reports.length === 0) return;
    setIsExporting(true);
    try {
      const blob = await pdf(
        <ReportPDF reports={reports} companyName={companyName} />,
      ).toBlob();

      const today = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildFileName({
        entityTitle: CONFIG.entityTitle,
        companyName,
        timestamp: today,
      });
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("[DefaultReportView] PDF export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="mx-auto px-8 pt-16 pb-8" style={{ maxWidth: 1000 }}>
          <div className="flex items-center justify-end mb-6">
            <div className="h-8 w-28 rounded-lg bg-innerBg animate-pulse" />
          </div>
          <div className="flex flex-col gap-4">
            {Array.from({ length: 6 }, (_, i) => (
              <TileSkeleton key={i} delay={i * 60} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 pt-14">
        <FileText
          className="w-8 h-8 text-zinc-300 dark:text-zinc-700"
          strokeWidth={1.5}
        />
        <p className="text-[13px] text-zinc-400 dark:text-zinc-600">
          Failed to load reports
        </p>
        <button
          onClick={loadReports}
          className="flex items-center gap-1.5 text-[12px] text-velBlue hover:underline"
        >
          <RefreshCw className="w-3 h-3" strokeWidth={2} />
          Try again
        </button>
      </div>
    );
  }

  /* ── Empty ── */
  if (reports.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 pt-14">
        <FileText
          className="w-8 h-8 text-zinc-300 dark:text-zinc-700"
          strokeWidth={1.5}
        />
        <p className="text-[14px] text-zinc-400 dark:text-zinc-600">
          No reports yet for {companyName || "this company"}.
        </p>
      </div>
    );
  }

  /* ── Populated ── */
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto px-8 pt-16 pb-8" style={{ maxWidth: 1000 }}>
        {/* ── Top bar ── */}
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={handleExportAll}
            disabled={isExporting}
            style={{
              background: t.isDark ? t.badgeBg : t.labelBg,
              border: `1px solid ${t.isDark ? t.badgeBorder : t.labelBorder}`,
              color: t.isDark ? t.badgeText : t.labelText,
            }}
            className="flex items-center gap-2 px-3 py-1.5 mt-4 rounded-lg text-[11px]
                       font-medium transition-opacity hover:opacity-70
                       active:opacity-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
            ) : (
              <FileDown className="h-3 w-3" strokeWidth={2} />
            )}
            {isExporting ? "Exporting…" : "Export PDF"}
          </button>
        </div>

        {/* ── Tiles ── */}
        <div className="flex flex-col gap-4">
          {reports.map((report, index) => (
            <div
              key={report.target_type || index}
              style={{
                transition: `opacity 0.4s ease-out ${Math.min(index * 55, 650)}ms, transform 0.4s ease-out ${Math.min(index * 55, 650)}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(10px)",
              }}
            >
              <TileReport data={report} t={t} companyName={companyName} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DefaultReportView;
